import initialState from './state'
import { loadHistoryRoutine, sendMessageRoutine, pollMessageRoutine, confirmActionRoutine, clearChatRoutine } from './actions'

export default function guruChatReducer(state = initialState, action) {
  switch (action.type) {
    // Reset chat state as soon as a context switch is requested (e.g. user
    // moves from a BP to a portfolio, or vice versa). Without this, an empty
    // history response would leave the previous context's messages visible —
    // i.e. portfolio Guru could display a single building's chat.
    case loadHistoryRoutine.TRIGGER:
      return { ...initialState }

    // Load history from backend and replace the list, but keep a trailing
    // optimistic user question that a concurrent in-flight send appended after
    // this load was triggered (e.g. a deep-linked ?ask on the Guru page), so the
    // question stays visible on top of the loaded history while it is answered.
    case loadHistoryRoutine.SUCCESS: {
      const loaded = (action.payload || []).map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        citations: msg.citations || [],
        toolsUsed: msg.toolsUsed || [],
        webSources: msg.webSources || [],
        actions: msg.actions || [],
        iterations: msg.iterations || 0,
        searchQuery: msg.searchQuery || '',
      }))
      const last = state.messages[state.messages.length - 1]
      const loadedEndsWithSameQuestion = loaded.length > 0
        && loaded[loaded.length - 1].role === 'user'
        && loaded[loaded.length - 1].content === last?.content
      const preserved = (last && last.role === 'user' && !loadedEndsWithSameQuestion) ? [last] : []
      return {
        ...state,
        messages: [...loaded, ...preserved],
      }
    }

    // Send message — add user message on TRIGGER so it's visible immediately
    case sendMessageRoutine.TRIGGER: {
      const { question } = action.payload || {}
      if (!question) return state
      return {
        ...state,
        messages: [
          ...state.messages,
          { role: 'user', content: question, timestamp: new Date().toISOString() },
        ],
      }
    }

    case sendMessageRoutine.REQUEST:
      return { ...state, isLoading: true, error: null, toolStatus: null, streamingText: null }

    case sendMessageRoutine.SUCCESS:
      return state

    case sendMessageRoutine.FAILURE:
      return { ...state, isLoading: false, error: action.payload }

    case sendMessageRoutine.FULFILL:
      return { ...state, isLoading: false }

    // Poll
    case pollMessageRoutine.REQUEST:
      return {
        ...state,
        toolStatus: action.payload?.toolStatus || state.toolStatus,
        streamingText: action.payload?.partial ?? state.streamingText,
      }

    case pollMessageRoutine.SUCCESS: {
      const { message, actions, citations, toolsUsed, webSources, iterations, searchQuery } = action.payload
      const assistantMessage = {
        role: 'assistant',
        content: message,
        citations: citations || [],
        actions: actions || [],
        toolsUsed: toolsUsed || [],
        webSources: webSources || [],
        iterations: iterations || 0,
        searchQuery: searchQuery || '',
        timestamp: new Date().toISOString(),
      }
      return {
        ...state,
        isLoading: false,
        toolStatus: null,
        streamingText: null,
        messages: [...state.messages, assistantMessage],
        activeAction: actions?.length > 0 ? actions[0] : null,
      }
    }

    case pollMessageRoutine.FAILURE: {
      const errorMessage = {
        role: 'assistant',
        content: action.payload?.message || 'Something went wrong. Please try again.',
        isError: true,
        timestamp: new Date().toISOString(),
      }
      return {
        ...state,
        isLoading: false,
        toolStatus: null,
        error: action.payload,
        messages: [...state.messages, errorMessage],
      }
    }

    // Confirm action
    case confirmActionRoutine.SUCCESS:
      return { ...state, activeAction: null }

    case confirmActionRoutine.FAILURE:
      return { ...state, error: action.payload }

    case clearChatRoutine.TRIGGER:
      return { ...initialState }

    default:
      return state
  }
}
